interface UrlGeneratorInputInterface {
  url: string;
  params?: Record<string, unknown>;
  query?: Record<string, unknown>;
}

/**
 * Converts an object to URL query parameters
 * Handles arrays, objects, and special characters
 */
const objectToQueryString = (obj: Record<string, unknown>): string => {
  const params = new URLSearchParams();

  const appendParam = (key: string, value: unknown) => {
    if (value === null || value === undefined) return;

    if (Array.isArray(value)) {
      value.forEach((item) => appendParam(`${key}[]`, item));
    } else if (typeof value === "object") {
      appendParam(key, JSON.stringify(value));
    } else {
      params.append(key, String(value));
    }
  };

  Object.entries(obj).forEach(([key, value]) => {
    appendParam(key, value);
  });

  return params.toString();
};

/**
 * Validates if the URL has a valid format
 */
const isValidUrl = (url: string): boolean => {
  try {
    new URL(url);
    return true;
  } catch {
    return false;
  }
};

/**
 * Generates a URL by replacing path parameters and appending query parameters
 * @throws {Error} If the URL format is invalid
 */
const generateUrl = ({ url, params, query }: UrlGeneratorInputInterface): string => {
  if (!url) {
    throw new Error("URL is required");
  }

  if (!url.includes("://")) {
    url = `https://${url}`;
  }

  if (!isValidUrl(url)) {
    throw new Error("Invalid URL format");
  }

  const urlObj = new URL(url);
  let path = urlObj.pathname;

  if (params) {
    Object.entries(params).forEach(([key, value]) => {
      if (value !== null && value !== undefined) {
        const paramValue = String(value);
        path = path.replace(`:${key}`, encodeURIComponent(paramValue));
      }
    });
  }

  path = path.replace(/:\w+/g, "");

  if (query) {
    const queryString = objectToQueryString(query);
    if (queryString) {
      urlObj.search = queryString;
    }
  }

  urlObj.pathname = path;

  const result = urlObj.toString().replace(/([^:]\/)\/+/g, "$1");

  return result;
};

export default generateUrl;
